/**
 * Integration form hook for managing integrations within assistant config
 */

import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'
import api from '@/lib/api'
import type { Integration, NewIntegration, MCPServerConfigFrontend, McpDiscoveryResponse } from '@/types/integrations'

/**
 * Hook for managing integrations in the assistant form
 */
export const useIntegrationForm = () => {
  const { setValue, getValues, watch } = useFormContext()
  const [isValidating, setIsValidating] = useState(false)

  // Watch config for real-time updates
  const config = watch('config') || {}

  /**
   * Validate integration with backend
   */
  const validateIntegration = async (integration: NewIntegration): Promise<boolean> => {
    try {
      const response = await api.post('/integration_validation/validate', {
        integration_name: integration.name,
        config: integration.config,
      })

      const data = response.data

      if (data.overall_status !== 'success') {
        const integrationResult = data.results?.find(
          (result: any) => result.integration_name === integration.name
        )

        if (integrationResult?.errors && integrationResult.errors.length > 0) {
          const errorMessages = integrationResult.errors.map((e: any) => e.message || e).join('; ')
          toast.error(`Validation failed: ${errorMessages}`)
        } else {
          toast.error(integrationResult?.message || data.message || 'Validation failed')
        }
        return false
      }

      const integrationResult = data.results?.find(
        (result: any) => result.integration_name === integration.name
      )

      if (!integrationResult || integrationResult.status !== 'success') {
        if (integrationResult?.errors && integrationResult.errors.length > 0) {
          const errorMessages = integrationResult.errors.map((e: any) => e.message || e).join('; ')
          toast.error(`Validation failed: ${errorMessages}`)
        } else {
          toast.error(integrationResult?.message || 'Validation failed')
        }
        return false
      }

      return true
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Validation failed'
      toast.error(`Validation failed: ${errorMessage}`)
      return false
    }
  }

  /**
   * Add a new integration
   */
  const addIntegration = async (newIntegration: NewIntegration): Promise<boolean> => {
    const formData = getValues()
    const currentTools = formData.config?.tools || []

    // Check if integration already exists
    const exists = currentTools.some(
      (tool: Integration) => tool && tool.name && tool.name === newIntegration.name
    )
    if (exists) {
      toast.error('This integration is already configured')
      return false
    }

    setIsValidating(true)
    try {
      // Validate integration with backend
      const isValid = await validateIntegration(newIntegration)
      if (!isValid) {
        return false
      }

      // Add to form
      const newTools = [...currentTools, newIntegration]
      const newConfig = { ...formData.config, tools: newTools }
      setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })

      toast.success('Integration added successfully')
      return true
    } catch (error) {
      toast.error('Failed to add integration')
      return false
    } finally {
      setIsValidating(false)
    }
  }

  /**
   * Update an existing integration
   */
  const updateIntegration = async (updatedIntegration: Integration): Promise<boolean> => {
    setIsValidating(true)
    try {
      // Validate integration with backend
      const isValid = await validateIntegration(updatedIntegration)
      if (!isValid) {
        return false
      }

      // Update in form
      const formData = getValues()
      const currentTools = formData.config?.tools || []
      const newTools = currentTools.map((t: Integration) =>
        t && t.name && t.name === updatedIntegration.name ? updatedIntegration : t
      )

      const newConfig = { ...formData.config, tools: newTools }
      setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })

      toast.success('Integration updated successfully')
      return true
    } catch (error) {
      toast.error('Failed to update integration')
      return false
    } finally {
      setIsValidating(false)
    }
  }

  /**
   * Delete an integration
   */
  const deleteIntegration = (integrationName: string) => {
    const formData = getValues()
    const currentTools = formData.config?.tools || []
    const newTools = currentTools.filter(
      (t: Integration) => t && t.name && t.name !== integrationName
    )

    const newConfig = { ...formData.config, tools: newTools }
    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })

    toast.success('Integration removed successfully')
  }

  /**
   * Get existing integrations
   */
  const getExistingIntegrations = (): Integration[] => {
    const formData = getValues()
    return formData?.config?.tools || []
  }

  // ===== MCP Server Management =====

  /**
   * Get MCP servers
   */
  const getMcpServers = (): MCPServerConfigFrontend[] => {
    const formData = getValues()
    return formData?.config?.mcp_servers || []
  }

  /**
   * Add MCP server
   */
  const addMcpServer = async (entry: MCPServerConfigFrontend): Promise<boolean> => {
    try {
      const formData = getValues()
      const currentServers: MCPServerConfigFrontend[] = formData?.config?.mcp_servers || []
      const newServers = [...currentServers, entry]
      const newConfig = { ...formData.config, mcp_servers: newServers }
      setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
      toast.success('MCP Server added successfully')
      return true
    } catch (e) {
      toast.error('Failed to add MCP server')
      return false
    }
  }

  /**
   * Update MCP server
   */
  const updateMcpServer = async (
    match: { index?: number; alias?: string; url?: string },
    entry: MCPServerConfigFrontend
  ): Promise<boolean> => {
    try {
      const formData = getValues()
      const servers: MCPServerConfigFrontend[] = formData?.config?.mcp_servers || []
      let idx = match.index ?? -1
      if (idx < 0) {
        idx = servers.findIndex(
          (s) =>
            (match.alias ? s.alias === match.alias : true) &&
            (match.url ? s.url === match.url : true)
        )
      }
      if (idx < 0 || idx >= servers.length) {
        toast.error('Failed to update MCP server')
        return false
      }
      const newServers = servers.map((s, i) => (i === idx ? entry : s))
      const newConfig = { ...formData.config, mcp_servers: newServers }
      setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
      toast.success('MCP Server updated successfully')
      return true
    } catch (e) {
      toast.error('Failed to update MCP server')
      return false
    }
  }

  /**
   * Delete MCP server
   */
  const deleteMcpServer = (match: { index?: number; alias?: string; url?: string }) => {
    const formData = getValues()
    const servers: MCPServerConfigFrontend[] = formData?.config?.mcp_servers || []
    let idx = match.index ?? -1
    if (idx < 0) {
      idx = servers.findIndex(
        (s) =>
          (match.alias ? s.alias === match.alias : true) && (match.url ? s.url === match.url : true)
      )
    }
    if (idx < 0 || idx >= servers.length) {
      toast.error('Failed to delete MCP server')
      return
    }
    const newServers = servers.filter((_, i) => i !== idx)
    const newConfig = { ...formData.config, mcp_servers: newServers }
    setValue('config', newConfig, { shouldDirty: true, shouldTouch: true })
    toast.success('MCP Server removed successfully')
  }

  return {
    // Integration methods
    addIntegration,
    updateIntegration,
    deleteIntegration,
    getExistingIntegrations,
    isValidating,
    // MCP methods
    getMcpServers,
    addMcpServer,
    updateMcpServer,
    deleteMcpServer,
    // Config access
    config,
  }
}

/**
 * Discover MCP server metadata
 */
export const discoverMcpServer = async (
  url: string,
  opts?: { forceRefresh?: boolean }
): Promise<McpDiscoveryResponse> => {
  const response = await api.post(
    `/mcp-server/discover${opts?.forceRefresh ? '?force_refresh=true' : ''}`,
    { url }
  )
  return response.data as McpDiscoveryResponse
}

/**
 * Hook for MCP discovery with caching
 */
export const useMcpDiscovery = (url: string | undefined) => {
  const [data, setData] = useState<McpDiscoveryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Simple fetch effect - in production you'd want to use TanStack Query
  const refetch = async () => {
    if (!url) return

    setIsLoading(true)
    setError(null)
    try {
      const result = await discoverMcpServer(url)
      setData(result)
    } catch (e) {
      setError(e as Error)
    } finally {
      setIsLoading(false)
    }
  }

  return { data, isLoading, error, refetch }
}
